import {
  COMPACT_TOOLTIP_STYLE,
  PARAGRAPH_TOOLTIP_STYLE,
} from '@src/components/common/tooltipStyles';
import { TOP_LAYER_VARS } from '@src/components/common/topLayerZ';
import {
  SAVED_SEGMENT_COLOR_PALETTE,
  savedSegmentColorName,
} from '@src/utility/savedSegmentColors';
import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import CreateIcon from '../../svgs/CreateIcon';
import DeleteIcon from '../../svgs/DeleteIcon';
import { EditPenIcon } from '../../svgs/EditPenIcon';
import HighlightIcon from '../../svgs/HighlightIcon';
import LineBreakIcon from '../../svgs/LineBreakIcon';
import MicrophoneIcon from '../../svgs/MicrophoneIcon';
import MuteKaraokeIcon from '../../svgs/MuteKaraokeIcon';
import OverlayGraphicIcon from '../../svgs/OverlayGraphicIcon';
import PasteTextIcon from '../../svgs/PasteTextIcon';
import ImageIcon from '../../svgs/quick-actions/ImageIcon';
import QuoteIcon from '../../svgs/quick-actions/QuoteIcon';
import ScissorsIcon from '../../svgs/quick-actions/ScissorsIcon';
import ReplaceIcon from '../../svgs/ReplaceIcon';
import ReplaceTermIcon from '../../svgs/ReplaceTermIcon';
import UndoIcon from '../../svgs/UndoIcon';
import ChevronRightIcon from '../../svgs/ChevronRightIcon';
import { useOutsideAlerter } from './useClickOutside';

import type {
  TranscriptContextMenuActions,
  TranscriptContextMenuDisabledReasons,
} from '../../types.ts/general';

const VIEWPORT_EDGE_MARGIN = 12;
const HEADER_SAFE_ZONE_PX = 80;
// Fixed width for the Highlight submenu flyout — keeps it stable regardless of
// row content (long names ellipsize) and gives a known width for the
// off-screen flip decision below.
const FLYOUT_WIDTH_PX = 300;
// A highlight appended to many times would otherwise grow a tooltip taller than
// the viewport; past a handful of passages the count is the useful part.
const MAX_TOOLTIP_PREVIEWS = 5;

export const TranscriptContextMenu: React.FC<{
  positionXY: { x: number; y: number };
  actions: Array<TranscriptContextMenuActions>;
  actionCallback: (action: TranscriptContextMenuActions | 'close') => void;
  overridenActionTitles?: Partial<Record<TranscriptContextMenuActions, string>>;
  /**
   * PLAT-11599 — actions that exist on this surface but are blocked by the
   * current state (e.g. Remove Text on a translated language). Each listed row
   * renders in its usual position, inert, with the reason as a hover tooltip.
   * Hiding a row is for surfaces that never offer it; this is for state.
   */
  disabledReasons?: TranscriptContextMenuDisabledReasons;
  /**
   * PLAT-12056 — the rows that hand off to Translation Review on this
   * surface instead of acting in place; they end in a chevron.
   */
  handoffActions?: readonly TranscriptContextMenuActions[];
  /**
   * Existing highlight groups listed in the "Highlight" submenu below the
   * color swatches — each appends the current selection to that group.
   */
  savedSegmentTargets?: Array<{
    key: string;
    name: string;
    color?: string;
    /**
     * First sentence of each passage the highlight covers, in transcript order.
     * Rows are named for their color, so this hover tooltip is where the
     * highlight's actual text lives.
     */
    previews?: string[];
  }>;
  onSaveToSegment?: (key: string) => void;
  /**
   * Create a new highlight for the current selection in the given color. When
   * provided, "Highlight" becomes a submenu leading with a row of color
   * swatches (picking one creates the highlight immediately); naming is a
   * secondary action in the hover popover.
   */
  onCreateNewSegment?: (color: string) => void;
}> = ({
  positionXY,
  actions,
  actionCallback,
  overridenActionTitles,
  disabledReasons,
  handoffActions,
  savedSegmentTargets,
  onSaveToSegment,
  onCreateNewSegment,
}) => {
  const menuConfig = [
    {
      action: 'replace',
      title: 'Replace Term',
      icon: (
        <ReplaceTermIcon
          width={'17'}
          height={'14'}
          strokeColor="currentColor"
        />
      ),
      extraClass: '',
      busy: false,
    },
    {
      action: 'saveAsClip',
      title: 'Save as Clip',
      icon: (
        <CreateIcon width={'16'} height={'14'} strokeColor="currentColor" />
      ),
      extraClass: '',
      busy: false,
    },
    {
      action: 'saveSegmentForLater',
      title: 'Highlight',
      icon: (
        <HighlightIcon width={'16'} height={'16'} strokeColor="currentColor" />
      ),
      extraClass: '',
      busy: false,
    },
    {
      action: 'saveAsPullQuote',
      title: 'Save as Pull Quote',
      icon: <QuoteIcon />,
      extraClass: '',
      busy: false,
    },
    {
      action: 'hideKaraoke',
      title: 'Hide Captions',
      icon: <MuteKaraokeIcon strokeColor="currentColor" />,
      extraClass: '',
      busy: false,
    },
    {
      action: 'showKaraoke',
      title: 'Show Captions',
      icon: <MuteKaraokeIcon strokeColor="currentColor" />,
      extraClass: '',
      busy: false,
    },
    {
      // PLAT-11914 — captions view: start a new line at this word, inside
      // the same caption.
      action: 'addLineBreak',
      title: 'Add Line Break',
      icon: <LineBreakIcon width="16" height="16" />,
      extraClass: '',
      busy: false,
    },
    {
      action: 'removeLineBreak',
      title: 'Remove Line Break',
      icon: <LineBreakIcon width="16" height="16" />,
      extraClass: '',
      busy: false,
    },
    {
      action: 'removeText',
      title: 'Remove Text',
      icon: <DeleteIcon strokeColor="currentColor" />,
      extraClass: 'remove-text',
      busy: false,
    },
    {
      action: 'removeTextLoading',
      title: 'Removing Text…',
      icon: <DeleteIcon strokeColor="currentColor" />,
      extraClass: 'remove-text',
      busy: true,
    },
    {
      action: 'removePhoto',
      title: 'Remove Photo',
      icon: <DeleteIcon strokeColor="currentColor" />,
      extraClass: 'remove-photo',
      busy: false,
    },
    {
      action: 'removePhotoLoading',
      title: 'Removing Photo…',
      icon: <DeleteIcon strokeColor="currentColor" />,
      extraClass: 'remove-photo',
      busy: true,
    },
    {
      action: 'addPhoto',
      title: 'Add Photo',
      icon: <ImageIcon />,
      extraClass: '',
      busy: false,
    },
    {
      action: 'addPhotoLoading',
      title: 'Adding Photo…',
      icon: <ImageIcon />,
      extraClass: '',
      busy: true,
    },
    {
      action: 'addOverlayGraphic',
      title: 'Add Overlay Text & Graphic',
      icon: (
        <OverlayGraphicIcon
          width={'16'}
          height={'16'}
          strokeColor="currentColor"
        />
      ),
      extraClass: '',
      busy: false,
    },
    {
      action: 'addOverlayGraphicLoading',
      title: 'Adding Overlay Text & Graphic…',
      icon: (
        <OverlayGraphicIcon
          width={'16'}
          height={'16'}
          strokeColor="currentColor"
        />
      ),
      extraClass: '',
      busy: true,
    },
    {
      action: 'restore',
      title: 'Restore',
      icon: (
        <ReplaceIcon width={'16'} height={'14'} strokeColor="currentColor" />
      ),
      extraClass: '',
      busy: false,
    },
    {
      action: 'cutText',
      title: 'Cut Text',
      icon: <ScissorsIcon />,
      extraClass: '',
      busy: false,
    },
    {
      action: 'pasteTextBefore',
      title: 'Paste Text',
      icon: (
        <PasteTextIcon width={'16'} height={'14'} strokeColor="currentColor" />
      ),
      extraClass: '',
      busy: false,
    },
    {
      action: 'pasteTextAfter',
      title: 'Paste After',
      icon: (
        <PasteTextIcon width={'16'} height={'14'} strokeColor="currentColor" />
      ),
      extraClass: '',
      busy: false,
    },
    {
      action: 'reassignSpeaker',
      title: 'Reassign Speaker for Selection',
      icon: <MicrophoneIcon width="16" height="14" fillColor="currentColor" />,
      extraClass: '',
      busy: false,
    },
    {
      // PLAT-10646 — translated prose view only: opens the phrase's
      // Fix Translation editor. Never included by the original-language
      // action builder.
      action: 'fixTranslation',
      title: 'Fix Translation',
      icon: <EditPenIcon size={16} />,
      extraClass: '',
      busy: false,
    },
    {
      // PLAT-10833 — translated prose view only, when the language has a dub:
      // re-records the selected words with the speaker's AI Voice (the same
      // row the selection menu offers in Translation Review, PLAT-10616).
      action: 'reDubAIVoice',
      title: 'Re-dub AI Voice',
      icon: <MicrophoneIcon width="16" height="14" fillColor="currentColor" />,
      extraClass: '',
      busy: false,
    },
    {
      action: 'undoReassignSpeaker',
      title: 'Undo Speaker Reassign',
      icon: <UndoIcon width={'16'} height={'14'} strokeColor="currentColor" />,
      extraClass: '',
      busy: false,
    },
  ];

  const wrapperRef = useRef<HTMLDivElement>(null);
  const previewTooltipId = useId();
  const disabledReasonTooltipId = useId();

  useOutsideAlerter(wrapperRef, () => actionCallback('close'));

  // PLAT-10266 (Card 8) — native-level Escape trap. The Translation Review
  // installs a window-level, capture-phase Escape handler that closes the WHOLE
  // review; this menu opens on right-click, which leaves focus on the document
  // body (the menu holds no input and suppresses mousedown so it never steals
  // focus), so a listener bound only to the menu wrapper would never see the
  // Escape. Listen on the window in the capture phase instead: Escape closes only
  // this menu and stops the event so background shortcuts / the review's own
  // Escape-to-close don't also fire. Mirrors the `CoveragePopover` trap
  // (`CoveragePopover.tsx:246-267`). `actionCallback` identity isn't stable
  // (callers pass an inline arrow), so read the latest via a ref.
  const actionCallbackRef = useRef(actionCallback);
  actionCallbackRef.current = actionCallback;
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        actionCallbackRef.current('close');
        return;
      }
      e.stopPropagation();
    };
    const stop = (e: KeyboardEvent) => {
      if (e.key === 'Escape') return;
      e.stopPropagation();
    };
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('keyup', stop, true);
    window.addEventListener('keypress', stop, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('keyup', stop, true);
      window.removeEventListener('keypress', stop, true);
    };
  }, []);

  // Position the menu at the click anchor, then nudge it back inside the
  // viewport on both axes. Measured after mount via useLayoutEffect — runs
  // before paint so the user never sees the off-screen position.
  //
  //  - Horizontal: shift left if the right edge would overflow (the anchor can
  //    sit near a right-side panel, e.g. the Content Studio speakers column).
  //  - Vertical: clamp the top so the menu never spills past the bottom edge,
  //    nor hides behind the header. A tall multi-block selection anchors near
  //    the bottom of the viewport — that is what pushed the menu off-screen.
  const [leftShiftPx, setLeftShiftPx] = useState(0);
  const [topPx, setTopPx] = useState(() => positionXY.y - 20);
  // The Highlight flyout opens to the right by default, but flips to the left
  // when the fixed-width flyout would spill past the right viewport edge (the
  // menu often sits next to the right-side speakers panel).
  const [flyoutFlipLeft, setFlyoutFlipLeft] = useState(false);
  useLayoutEffect(() => {
    const node = wrapperRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();

    const maxRight = window.innerWidth - VIEWPORT_EDGE_MARGIN;
    const newLeftShift = rect.right > maxRight ? rect.right - maxRight : 0;
    setLeftShiftPx(newLeftShift);

    const menuRight = Math.max(8, positionXY.x - newLeftShift) + rect.width;
    setFlyoutFlipLeft(
      menuRight + FLYOUT_WIDTH_PX + VIEWPORT_EDGE_MARGIN > window.innerWidth,
    );

    const anchorTop = positionXY.y - 20;
    const maxTop = window.innerHeight - VIEWPORT_EDGE_MARGIN - rect.height;
    setTopPx(
      Math.min(
        Math.max(anchorTop, HEADER_SAFE_ZONE_PX),
        Math.max(HEADER_SAFE_ZONE_PX, maxTop),
      ),
    );
  }, [positionXY.x, positionXY.y, actions.length]);

  function getMenuItem(action: TranscriptContextMenuActions) {
    const menuItemConfig = menuConfig.find(item => item.action === action);

    if (!menuItemConfig) return null;

    // aria-disabled rather than native disabled (a div anyway): the row must
    // keep taking pointer events so the tooltip saying WHY can fire. The click
    // is suppressed here instead.
    const disabledReason = disabledReasons?.[action];
    // A busy row ("Adding Photo…") is in progress, not blocked: it keeps its
    // wait cursor rather than not-allowed, but a second click must not start
    // a second job (PLAT-11603).
    const busy = menuItemConfig.busy;
    // PLAT-12052 / PLAT-12056 — a row the host hands off to Translation
    // Review (rather than acting here) ends in a chevron, the menu's own
    // "this leads on" mark, and names its destination for assistive tech.
    const handsOff = handoffActions?.includes(action) ?? false;
    const trailing = handsOff ? (
      <ChevronRightIcon width="12" height="12" />
    ) : null;
    const ariaLabel = handsOff
      ? `${menuItemConfig.title}, opens Translation Review`
      : undefined;
    return (
      <MenuItem
        $busy={busy}
        aria-busy={busy ? true : undefined}
        aria-disabled={disabledReason ? true : undefined}
        onMouseDown={e => e.preventDefault()}
        onClick={e => {
          e.stopPropagation();
          if (disabledReason || busy) return;
          actionCallback(action);
        }}
        className={menuItemConfig.extraClass}
        data-testid={`transcript-context-action-${action}`}
        data-tooltip-id={disabledReason ? disabledReasonTooltipId : undefined}
        data-tooltip-content={disabledReason}
        aria-label={ariaLabel}
      >
        <div className="icon">{menuItemConfig.icon}</div>
        <span>{overridenActionTitles?.[action] || menuItemConfig.title}</span>
        {trailing && (
          <span
            className="trailing"
            aria-hidden="true"
            data-testid="transcript-context-trailing"
          >
            {trailing}
          </span>
        )}
      </MenuItem>
    );
  }

  return (
    <MenuWrapper
      positionXY={positionXY}
      $leftShiftPx={leftShiftPx}
      $topPx={topPx}
      ref={wrapperRef}
      onMouseUp={e => e.stopPropagation()}
      data-testid="transcript-context-menu"
    >
      {actions.map(action => {
        // "Highlight" expands into a submenu leading with color swatches:
        // picking a color creates a new highlight immediately, and any
        // existing groups follow below as "append to" targets.
        if (action === 'saveSegmentForLater' && onCreateNewSegment) {
          const hasTargets = !!savedSegmentTargets?.length;
          // Only offer palette colors not already taken by an existing
          // highlight, so each highlight keeps a distinct color.
          const usedColors = new Set(
            savedSegmentTargets?.map(t => t.color).filter(Boolean),
          );
          const availableColors = SAVED_SEGMENT_COLOR_PALETTE.filter(
            color => !usedColors.has(color),
          );
          return (
            <SubmenuParent
              key={action}
              data-testid="transcript-context-save-segment-submenu"
            >
              <MenuItem onMouseDown={e => e.preventDefault()}>
                <div className="icon">
                  <HighlightIcon
                    width={'16'}
                    height={'16'}
                    strokeColor="currentColor"
                  />
                </div>
                <span>Highlight</span>
                <SubmenuCaret>▶</SubmenuCaret>
              </MenuItem>
              <Flyout className="submenu" $flipLeft={flyoutFlipLeft}>
                {availableColors.length > 0 && (
                  <NewHighlightRow
                    $divider={hasTargets}
                    data-testid="transcript-context-create-new-segment"
                  >
                    <NewHighlightLabel>New</NewHighlightLabel>
                    <SwatchList>
                      {availableColors.map(color => (
                        <ColorSwatchButton
                          key={color}
                          type="button"
                          $color={color}
                          title={`New Highlight in ${savedSegmentColorName(color)}`}
                          aria-label={`New Highlight in ${savedSegmentColorName(color)}`}
                          onMouseDown={e => e.preventDefault()}
                          onClick={e => {
                            e.stopPropagation();
                            onCreateNewSegment(color);
                          }}
                          data-testid={`transcript-context-create-new-segment-${color}`}
                        />
                      ))}
                    </SwatchList>
                  </NewHighlightRow>
                )}
                {/* Names the list below, so it reads as a second action
                    rather than an unlabeled tail to the New row (PLAT-11182).
                    Parallel to "New", and it names the action — clicking a row
                    appends the current selection to that highlight. */}
                {hasTargets && <SectionLabel>Add To</SectionLabel>}
                {/* Pick-a-highlight only. Renaming moved to the Highlights
                    dropdown's per-row menu (PLAT-11182), where the rest of
                    highlight management lives — this submenu's job is to file
                    the current selection into one. */}
                {savedSegmentTargets?.map(target => (
                  <MenuItem
                    key={target.key}
                    onMouseDown={e => e.preventDefault()}
                    onClick={e => {
                      e.stopPropagation();
                      onSaveToSegment?.(target.key);
                    }}
                    data-testid={`transcript-context-save-to-segment-${target.key}`}
                    data-tooltip-id={
                      target.previews?.length ? previewTooltipId : undefined
                    }
                    data-segment-key={target.key}
                  >
                    {target.color && (
                      <SegmentColorSwatch $color={target.color} />
                    )}
                    <SegmentName>{target.name}</SegmentName>
                  </MenuItem>
                ))}
              </Flyout>
            </SubmenuParent>
          );
        }
        return <div key={action}>{getMenuItem(action)}</div>;
      })}
      {/* One-sentence "what unlocks this" hint for state-disabled rows
          (PLAT-11599). Portaled to <body>: rendered inline it would sit in
          this menu's stacking context, and inside the clip preview (whose
          modal wrapper's backdrop-filter makes `fixed` resolve against the
          modal — CLAUDE.md pitfall 19) the video pane painted over it and the
          transcript column clipped it. The compact style's top-layer z only
          counts from <body>. */}
      {createPortal(
        <Tooltip
          id={disabledReasonTooltipId}
          place="right"
          positionStrategy="fixed"
          opacity={1}
          noArrow
          style={COMPACT_TOOLTIP_STYLE}
        />,
        document.body,
      )}
      <Tooltip
        id={previewTooltipId}
        place="right"
        positionStrategy="fixed"
        opacity={1}
        // Rendered rather than passed as `data-tooltip-content` so a highlight
        // that was appended to can list every passage it covers, each on its
        // own line under the highlight's color.
        render={({ activeAnchor }) => {
          const target = savedSegmentTargets?.find(
            t => t.key === activeAnchor?.getAttribute('data-segment-key'),
          );
          const previews = target?.previews ?? [];
          if (previews.length === 0) return null;
          return (
            <PreviewList>
              {previews.slice(0, MAX_TOOLTIP_PREVIEWS).map((preview, i) => (
                <PreviewItem key={i} $color={target?.color}>
                  {preview}
                </PreviewItem>
              ))}
              {previews.length > MAX_TOOLTIP_PREVIEWS && (
                <PreviewMore>
                  +{previews.length - MAX_TOOLTIP_PREVIEWS} more
                </PreviewMore>
              )}
            </PreviewList>
          );
        }}
        style={{
          ...PARAGRAPH_TOOLTIP_STYLE,
          maxWidth: '280px',
          wordBreak: 'break-word',
          // Sit above the context menu (resting z 1000011); lifts with the
          // tooltip layer var inside the clip-preview review workspace.
          zIndex: `var(${TOP_LAYER_VARS.tooltip}, 1000021)`,
        }}
      />
    </MenuWrapper>
  );
};

const MenuWrapper = styled.div.attrs(
  (props: {
    positionXY: { x: number; y: number };
    $leftShiftPx: number;
    $topPx: number;
  }) => props,
)`
  display: flex;
  flex-direction: column;
  position: fixed;
  /* Resting z is the historical 1000011; only the clip-preview review lifts it
     (via --top-layer-context-menu-z) above that workspace. At rest unchanged
     (PLAT-10286). */
  z-index: var(${TOP_LAYER_VARS.contextMenu}, 1000011);

  background: var(--background);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--elevation-toast);
  font-size: var(--text-xs);
  gap: 2px;
  padding: var(--spacing-xs);

  /* Anchor + viewport clamping are computed in the layout effect above. */
  left: ${props => Math.max(8, props.positionXY.x - props.$leftShiftPx)}px;
  top: ${props => props.$topPx}px;
`;

const MenuItem = styled.div<{
  $busy?: boolean;
}>`
  cursor: pointer;
  padding: 6px 10px;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  gap: 8px;
  justify-content: flex-start;
  align-items: center;
  color: var(--foreground);

  span {
    white-space: nowrap;
  }
  /* PLAT-12052 — a row that leads elsewhere ends in a chevron, quiet and at
     the right edge. */
  .trailing {
    display: flex;
    margin-left: auto;
    padding-left: 12px;
    color: var(--text-muted);
  }
  .icon {
    display: flex;
    flex-shrink: 0;
    svg {
      width: 16px;
      height: 16px;
    }
  }

  /* Destructive actions keep their semantic red hover */
  &.remove-photo:hover,
  &.remove-text:hover {
    background-color: var(--destructive);
    color: var(--destructive-foreground);
  }

  border-radius: var(--radius);

  &:first-child {
    border-top-right-radius: var(--radius);
    border-top-left-radius: var(--radius);
  }

  &:last-child {
    border-bottom-right-radius: var(--radius);
    border-bottom-left-radius: var(--radius);
  }

  /* Default hover: subtle surface elevation — primary (green) is for action buttons only */
  &:hover {
    background-color: var(--surface-elevated);
  }

  /* In-progress row: dimmed with a wait cursor; the click is suppressed in
     the handler (PLAT-11603). */
  ${props => (props.$busy ? 'cursor: wait; opacity: 0.5;' : '')}

  /* State-disabled row (PLAT-11599): muted, not-allowed, no hover fill — the
     destructive red hover included. Keyed off aria-disabled so the selector
     outranks the class hovers above by source order at equal specificity. */
  &[aria-disabled='true'],
  &[aria-disabled='true']:hover {
    background-color: transparent;
    color: var(--text-muted);
    cursor: not-allowed;
  }
`;

// Color swatches are the primary action of the Highlight submenu: picking a
// color creates a new highlight. The row sits at the top, with a divider
// separating it from the list of existing segments below (when any exist).
// The swatch list wraps, so the label aligns to the first swatch row rather
// than floating in the vertical center of a two-row block.
const NewHighlightRow = styled.div<{ $divider?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 10px;
  ${props => (props.$divider ? 'border-bottom: 1px solid var(--border);' : '')}
`;

const NewHighlightLabel = styled.span`
  color: var(--muted-foreground, var(--foreground));
  white-space: nowrap;
  /* Match the swatch height so the label sits on the first row's baseline. */
  line-height: 16px;
`;

/* Heads the existing-highlights list. Indented to the same x as "New" and in
   the same muted color, so the two read as siblings; the rows below carry
   their own hover, so this one must not look interactive. */
const SectionLabel = styled.div`
  padding: 8px 10px 2px;
  color: var(--muted-foreground, var(--foreground));
  white-space: nowrap;
`;

// Wraps: the full palette is wider than the fixed-width flyout, and the
// swatches themselves are `flex-shrink: 0`, so without wrapping the tail of the
// palette would be clipped.
const SwatchList = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
`;

const ColorSwatchButton = styled.button<{ $color: string }>`
  width: 16px;
  height: 16px;
  padding: 0;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  background-color: ${props => props.$color};
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    outline: 2px solid var(--foreground);
    outline-offset: 1px;
  }
`;

const SubmenuParent = styled.div`
  position: relative;

  &:hover > .submenu {
    display: flex;
  }
`;

const SubmenuCaret = styled.span`
  margin-left: auto;
  padding-left: 12px;
  font-size: 9px;
  opacity: 0.7;
`;

const Flyout = styled.div<{ $forceOpen?: boolean; $flipLeft?: boolean }>`
  display: ${props => (props.$forceOpen ? 'flex' : 'none')};
  position: absolute;
  left: ${props => (props.$flipLeft ? 'auto' : '100%')};
  right: ${props => (props.$flipLeft ? '100%' : 'auto')};
  top: 0;
  flex-direction: column;
  width: ${FLYOUT_WIDTH_PX}px;
  max-height: 240px;
  overflow-y: auto;
  background: var(--background);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--elevation-toast);
  /* Sit above the parent menu; share its z so it never hides behind siblings. */
  z-index: var(${TOP_LAYER_VARS.contextMenu}, 1000011);
`;

// Highlight name in a flyout row — takes the remaining width and ellipsizes so
// a long name can't stretch the fixed-width flyout. Full text is on hover via
// the row's preview tooltip.
const SegmentName = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Hover tooltip listing what a highlight actually says — one line per passage,
// each carrying the highlight's color so the tooltip reads as that highlight's.
const PreviewList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PreviewItem = styled.div<{ $color?: string }>`
  border-left: 2px solid ${props => props.$color ?? 'var(--border)'};
  padding-left: 8px;
`;

const PreviewMore = styled.div`
  opacity: 0.6;
`;

// Per-segment color indicator shown before each segment name in the "Save to
// {name}" list, matching the segment's transcript underline color.
const SegmentColorSwatch = styled.span<{ $color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 3px;
  flex-shrink: 0;
  background-color: ${props => props.$color};
`;
